import React from 'react';

// YouTube utility functions
export const extractYouTubeId = (url) => {
  const patterns = [
    /(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?v=([a-zA-Z0-9_-]+)/,
    /(?:https?:\/\/)?(?:www\.)?youtube\.com\/embed\/([a-zA-Z0-9_-]+)/,
    /(?:https?:\/\/)?youtu\.be\/([a-zA-Z0-9_-]+)/,
    /(?:https?:\/\/)?(?:www\.)?youtube\.com\/v\/([a-zA-Z0-9_-]+)/
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) {
      return match[1];
    }
  }
  return null;
};

export const isValidYouTubeUrl = (url) => {
  return extractYouTubeId(url) !== null;
};

export const getYouTubeThumbnail = (videoId, quality = 'maxresdefault') => {
  return `https://img.youtube.com/vi/${videoId}/${quality}.jpg`;
};

// YouTube Embed Component
const YouTubeEmbed = ({ videoUrl, title = "YouTube Video", className = "" }) => {
  const videoId = extractYouTubeId(videoUrl);
  
  if (!videoId) {
    return (
      <div className={`bg-gray-100 rounded-lg p-4 border-2 border-dashed border-gray-300 ${className}`}>
        <div className="text-center">
          <div className="text-4xl mb-2">❌</div>
          <p className="text-gray-600 font-medium">Invalid YouTube URL</p>
          <p className="text-sm text-gray-500 mt-1">Please check the video link</p>
        </div>
      </div>
    );
  }

  const embedUrl = `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&showinfo=0`;

  return (
    <div className={`relative w-full ${className}`}>
      <div className="relative w-full h-0 pb-[56.25%]"> {/* 16:9 aspect ratio */}
        <iframe
          src={embedUrl}
          title={title}
          className="absolute top-0 left-0 w-full h-full rounded-lg border border-gray-300 shadow-lg"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
      
      {/* Video Info */}
      <div className="mt-3 flex items-center justify-between text-sm text-gray-600">
        <div className="flex items-center space-x-2">
          <span className="text-red-500">📺</span>
          <span>YouTube Video</span>
        </div>
        <a
          href={videoUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 font-medium"
        >
          Watch on YouTube →
        </a>
      </div>
    </div>
  );
};

// YouTube Preview Component (for CreatePost)
export const YouTubePreview = ({ videoUrl, className = "" }) => {
  const videoId = extractYouTubeId(videoUrl);
  
  if (!videoId) {
    return null;
  }

  const thumbnailUrl = getYouTubeThumbnail(videoId);

  return (
    <div className={`relative bg-gray-100 rounded-lg overflow-hidden ${className}`}>
      <div className="relative w-full h-48">
        <img 
          src={thumbnailUrl} 
          alt="YouTube Video Thumbnail"
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.src = 'https://img.youtube.com/vi/' + videoId + '/hqdefault.jpg';
          }}
        />
        <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
          <div className="bg-red-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2">
            <span className="text-2xl">▶️</span>
            <span className="font-medium">YouTube Video</span>
          </div>
        </div>
      </div>
      <div className="p-3 bg-white">
        <p className="text-sm text-gray-600">
          🎬 Video will be embedded in your post
        </p>
      </div>
    </div>
  );
};

export default YouTubeEmbed;